import { create } from 'zustand';

/**
 * Singleton toast queue. One toast visible at a time — if a second
 * toast fires while the first is still showing, the first is replaced.
 * Feels more honest than queueing and showing stale messages on the
 * hot path (someone rapidly swapping themes shouldn't see a
 * 6-second-long cascade of "Default applied, Luce applied, ...").
 *
 * Usage:
 *   import { useToastStore } from '@/store/toast';
 *   useToastStore.getState().show('Midnight applied');
 *
 * Inside a component that needs to react to the current toast:
 *   const current = useToastStore((s) => s.current);
 *
 * Tone is advisory — the Toast primitive picks accent / warn / error
 * treatment off the `tone` field. Default is 'neutral' which uses
 * --evi-text on --evi-surface-2.
 */
export type ToastTone = 'neutral' | 'accent' | 'warn' | 'error';

export interface ToastPayload {
  id: number;
  message: string;
  tone: ToastTone;
}

interface ToastState {
  current: ToastPayload | null;
  show: (message: string, options?: { tone?: ToastTone; durationMs?: number }) => void;
  dismiss: () => void;
}

const DEFAULT_DURATION_MS = 1800;

let nextId = 1;
let pendingTimer: ReturnType<typeof setTimeout> | null = null;

export const useToastStore = create<ToastState>((set) => ({
  current: null,
  show: (message, options) => {
    const tone: ToastTone = options?.tone ?? 'neutral';
    const duration = options?.durationMs ?? DEFAULT_DURATION_MS;
    const id = nextId++;

    if (pendingTimer) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }

    set({ current: { id, message, tone } });

    pendingTimer = setTimeout(() => {
      pendingTimer = null;
      // Only clear if this toast is still the current one; guards
      // against a race where a later toast replaced us between show()
      // and the timer firing.
      set((state) => (state.current?.id === id ? { current: null } : state));
    }, duration);
  },
  dismiss: () => {
    if (pendingTimer) {
      clearTimeout(pendingTimer);
      pendingTimer = null;
    }
    set({ current: null });
  },
}));

/**
 * Imperative helper for callers outside React render trees (e.g.
 * React-Query onSuccess handlers). Inside components, prefer reading
 * `useToastStore` directly for the usual reactive hygiene.
 */
export function toast(message: string, options?: { tone?: ToastTone; durationMs?: number }): void {
  useToastStore.getState().show(message, options);
}
